import { useState, useEffect, useCallback, useRef, FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useToastStore } from '../../store/toastStore';
import { useAuthStore } from '../../store/authStore';

// --- Types ---

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role: 'root' | 'company_admin' | 'agent' | 'end_user';
  company_name: string | null;
  is_active: boolean;
  created_at: string;
}

interface UsersSearchResponse {
  data: User[];
  meta: { total: number; page: number; page_size: number };
}

interface EditFormData {
  first_name: string;
  last_name: string;
  email: string;
  role: string;
}

interface PasswordFormData {
  new_password: string;
  confirm_password: string;
}

// --- Constants ---

const PAGE_SIZE = 20;

const ROOT_ROLE_OPTIONS = [
  { value: '', label: 'Todos los roles' },
  { value: 'root', label: 'Root' },
  { value: 'company_admin', label: 'Admin Empresa' },
  { value: 'agent', label: 'Agente' },
  { value: 'end_user', label: 'Usuario Final' },
] as const;

const COMPANY_ADMIN_ROLE_OPTIONS = [
  { value: '', label: 'Todos los roles' },
  { value: 'agent', label: 'Agente' },
  { value: 'end_user', label: 'Usuario Final' },
] as const;

const EDITABLE_ROLES = [
  { value: 'root', label: 'Root' },
  { value: 'company_admin', label: 'Admin Empresa' },
  { value: 'agent', label: 'Agente' },
  { value: 'end_user', label: 'Usuario Final' },
] as const;

const ROLE_BADGE_CLASS: Record<string, string> = {
  root: 'badge-error',
  company_admin: 'badge-primary',
  agent: 'badge-info',
  end_user: 'badge-ghost',
};

const ROLE_LABELS: Record<string, string> = {
  root: 'Root',
  company_admin: 'Admin Empresa',
  agent: 'Agente',
  end_user: 'Usuario Final',
};

// --- Helpers ---

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-AR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function getInitials(firstName: string, lastName: string): string {
  const first = firstName?.charAt(0)?.toUpperCase() || '';
  const last = lastName?.charAt(0)?.toUpperCase() || '';
  return first + last || '??';
}

function getAvatarColor(name: string): string {
  const colors = [
    'bg-primary text-primary-content',
    'bg-secondary text-secondary-content',
    'bg-accent text-accent-content',
    'bg-info text-info-content',
    'bg-success text-success-content',
    'bg-warning text-warning-content',
    'bg-error text-error-content',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

// --- Skeleton Row ---

function SkeletonRow(): React.ReactElement {
  return (
    <tr className="animate-pulse">
      <td>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-base-300 rounded-full" />
          <div className="h-4 bg-base-300 rounded w-28" />
        </div>
      </td>
      <td><div className="h-4 bg-base-300 rounded w-40" /></td>
      <td><div className="h-5 bg-base-300 rounded-full w-20" /></td>
      <td><div className="h-4 bg-base-300 rounded w-16" /></td>
      <td><div className="h-4 bg-base-300 rounded w-24" /></td>
      <td><div className="h-6 bg-base-300 rounded w-8" /></td>
    </tr>
  );
}

// --- Mobile Card Skeleton ---

function SkeletonCard(): React.ReactElement {
  return (
    <div className="card bg-base-100 shadow-sm animate-pulse">
      <div className="card-body p-4">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 bg-base-300 rounded-full" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-base-300 rounded w-32" />
            <div className="h-3 bg-base-300 rounded w-48" />
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Edit Modal ---

function EditModal({
  user,
  isRoot,
  isOpen,
  onClose,
  onSave,
  isSaving,
}: {
  user: User | null;
  isRoot: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: EditFormData) => void;
  isSaving: boolean;
}): React.ReactElement | null {
  const [form, setForm] = useState<EditFormData>({
    first_name: '',
    last_name: '',
    email: '',
    role: '',
  });

  useEffect(() => {
    if (user) {
      setForm({
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        role: user.role,
      });
    }
  }, [user]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSave(form);
  };

  if (!isOpen || !user) return null;

  return (
    <dialog className="modal modal-open">
      <div className="modal-box">
        <h3 className="font-bold text-lg mb-4">Editar usuario</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="form-control">
            <label className="label">
              <span className="label-text">Nombre</span>
            </label>
            <input
              type="text"
              className="input input-bordered w-full"
              value={form.first_name}
              onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
              required
            />
          </div>
          <div className="form-control">
            <label className="label">
              <span className="label-text">Apellido</span>
            </label>
            <input
              type="text"
              className="input input-bordered w-full"
              value={form.last_name}
              onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
              required
            />
          </div>
          <div className="form-control">
            <label className="label">
              <span className="label-text">Email</span>
            </label>
            <input
              type="email"
              className="input input-bordered w-full"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              required
            />
          </div>
          <div className="form-control">
            <label className="label">
              <span className="label-text">Rol</span>
            </label>
            <select
              className="select select-bordered w-full"
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              disabled={!isRoot}
            >
              {EDITABLE_ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            {!isRoot && (
              <label className="label">
                <span className="label-text-alt text-base-content/50">
                  Solo un administrador root puede cambiar roles
                </span>
              </label>
            )}
          </div>
          <div className="modal-action">
            <button type="button" className="btn" onClick={onClose} disabled={isSaving}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSaving}>
              {isSaving && <span className="loading loading-spinner loading-xs" />}
              Guardar
            </button>
          </div>
        </form>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button type="button" onClick={onClose}>close</button>
      </form>
    </dialog>
  );
}

// --- Password Modal ---

function PasswordModal({
  user,
  isOpen,
  onClose,
  onSave,
  isSaving,
}: {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (newPassword: string) => void;
  isSaving: boolean;
}): React.ReactElement | null {
  const [form, setForm] = useState<PasswordFormData>({
    new_password: '',
    confirm_password: '',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setForm({ new_password: '', confirm_password: '' });
      setError('');
    }
  }, [isOpen]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (form.new_password.length < 8) {
      setError('La contrasena debe tener al menos 8 caracteres');
      return;
    }
    if (form.new_password !== form.confirm_password) {
      setError('Las contrasenas no coinciden');
      return;
    }
    setError('');
    onSave(form.new_password);
  };

  if (!isOpen || !user) return null;

  return (
    <dialog className="modal modal-open">
      <div className="modal-box">
        <h3 className="font-bold text-lg mb-1">Cambiar contrasena</h3>
        <p className="text-base-content/60 text-sm mb-4">
          {user.first_name} {user.last_name} ({user.email})
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="form-control">
            <label className="label">
              <span className="label-text">Nueva contrasena</span>
            </label>
            <input
              type="password"
              className="input input-bordered w-full"
              value={form.new_password}
              onChange={(e) => {
                setForm((f) => ({ ...f, new_password: e.target.value }));
                setError('');
              }}
              placeholder="Minimo 8 caracteres"
              required
              minLength={8}
            />
          </div>
          <div className="form-control">
            <label className="label">
              <span className="label-text">Confirmar contrasena</span>
            </label>
            <input
              type="password"
              className={`input input-bordered w-full ${error ? 'input-error' : ''}`}
              value={form.confirm_password}
              onChange={(e) => {
                setForm((f) => ({ ...f, confirm_password: e.target.value }));
                setError('');
              }}
              placeholder="Repetir contrasena"
              required
              minLength={8}
            />
            {error && (
              <label className="label">
                <span className="label-text-alt text-error">{error}</span>
              </label>
            )}
          </div>
          <div className="modal-action">
            <button type="button" className="btn" onClick={onClose} disabled={isSaving}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSaving}>
              {isSaving && <span className="loading loading-spinner loading-xs" />}
              Cambiar contrasena
            </button>
          </div>
        </form>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button type="button" onClick={onClose}>close</button>
      </form>
    </dialog>
  );
}

// --- Actions Dropdown ---

function ActionsDropdown({
  user: targetUser,
  onEdit,
  onChangePassword,
  onToggleActive,
  isToggling,
}: {
  user: User;
  onEdit: () => void;
  onChangePassword: () => void;
  onToggleActive: () => void;
  isToggling: boolean;
}): React.ReactElement {
  const detailsRef = useRef<HTMLDetailsElement>(null);

  const close = () => {
    if (detailsRef.current) detailsRef.current.open = false;
  };

  return (
    <details ref={detailsRef} className="dropdown dropdown-end">
      <summary className="btn btn-ghost btn-sm btn-square">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </summary>
      <ul className="dropdown-content z-[1] menu p-2 shadow-lg bg-base-100 rounded-box w-52 border border-base-200">
        <li>
          <button
            onClick={() => {
              close();
              onEdit();
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
            Editar
          </button>
        </li>
        <li>
          <button
            onClick={() => {
              close();
              onChangePassword();
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            Cambiar contrasena
          </button>
        </li>
        <li>
          <button
            onClick={() => {
              close();
              onToggleActive();
            }}
            disabled={isToggling}
            className={targetUser.is_active ? 'text-error' : 'text-success'}
          >
            {targetUser.is_active ? (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
                </svg>
                Desactivar
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Activar
              </>
            )}
          </button>
        </li>
      </ul>
    </details>
  );
}

// --- Mobile User Card ---

function UserCard({
  user: targetUser,
  onEdit,
  onChangePassword,
  onToggleActive,
  isToggling,
}: {
  user: User;
  onEdit: () => void;
  onChangePassword: () => void;
  onToggleActive: () => void;
  isToggling: boolean;
}): React.ReactElement {
  return (
    <div className="card bg-base-100 shadow-sm">
      <div className="card-body p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${getAvatarColor(
                targetUser.first_name + targetUser.last_name
              )}`}
            >
              {getInitials(targetUser.first_name, targetUser.last_name)}
            </div>
            <div className="min-w-0">
              <p className="font-semibold truncate">
                {targetUser.first_name} {targetUser.last_name}
              </p>
              <p className="text-sm text-base-content/60 truncate">{targetUser.email}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`badge badge-sm ${ROLE_BADGE_CLASS[targetUser.role] || 'badge-ghost'}`}>
                  {ROLE_LABELS[targetUser.role] || targetUser.role}
                </span>
                <div className="flex items-center gap-1">
                  <span
                    className={`inline-block h-2 w-2 rounded-full ${
                      targetUser.is_active ? 'bg-success' : 'bg-error'
                    }`}
                  />
                  <span className="text-xs text-base-content/60">
                    {targetUser.is_active ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <ActionsDropdown
            user={targetUser}
            onEdit={onEdit}
            onChangePassword={onChangePassword}
            onToggleActive={onToggleActive}
            isToggling={isToggling}
          />
        </div>
        <div className="text-xs text-base-content/40 mt-2">
          Creado: {formatDate(targetUser.created_at)}
        </div>
      </div>
    </div>
  );
}

// --- Main Component ---

export default function UsersPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);
  const authUser = useAuthStore((s) => s.user);
  const isRoot = authUser?.role === 'root';

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(0);

  // Modal state
  const [editUser, setEditUser] = useState<User | null>(null);
  const [passwordUser, setPasswordUser] = useState<User | null>(null);

  const debouncedSearch = useDebounce(search, 350);

  const roleOptions = isRoot ? ROOT_ROLE_OPTIONS : COMPANY_ADMIN_ROLE_OPTIONS;

  // Reset to first page when filters change
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(0);
  }, []);

  const handleRoleChange = useCallback((value: string) => {
    setRoleFilter(value);
    setPage(0);
  }, []);

  // --- Query ---

  const { data: usersData, isLoading, isFetching } = useQuery({
    queryKey: ['admin-users', debouncedSearch, roleFilter, page],
    queryFn: () =>
      api.get<UsersSearchResponse>('/v1/users/search', {
        ...(debouncedSearch ? { q: debouncedSearch } : {}),
        ...(roleFilter ? { role: roleFilter } : {}),
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
    placeholderData: (prev) => prev,
  });

  const users = usersData?.data ?? [];
  const totalCount = usersData?.meta?.total ?? 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // --- Toggle Active Mutation ---

  const toggleActiveMutation = useMutation({
    mutationFn: (userId: number) =>
      api.patch<{ data: User }>(`/v1/users/${userId}/toggle-active`),
    onSuccess: (_data, userId) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      const u = users.find((usr) => usr.id === userId);
      const action = u?.is_active ? 'desactivado' : 'activado';
      addToast(`Usuario ${action} correctamente`, 'success');
    },
    onError: () => {
      addToast('Error al cambiar el estado del usuario', 'error');
    },
  });

  // --- Edit Mutation ---

  const editMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: number; data: EditFormData }) => {
      const payload: Record<string, string> = {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
      };
      if (isRoot) {
        payload.role = data.role;
      }
      return api.patch<{ data: User }>(`/v1/users/${userId}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      addToast('Usuario actualizado correctamente', 'success');
      setEditUser(null);
    },
    onError: () => {
      addToast('Error al actualizar el usuario', 'error');
    },
  });

  // --- Password Mutation ---

  const passwordMutation = useMutation({
    mutationFn: ({ userId, newPassword }: { userId: number; newPassword: string }) =>
      api.patch(`/v1/users/${userId}/password`, { new_password: newPassword }),
    onSuccess: () => {
      addToast('Contrasena cambiada correctamente', 'success');
      setPasswordUser(null);
    },
    onError: () => {
      addToast('Error al cambiar la contrasena', 'error');
    },
  });

  // --- Render ---

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold">Usuarios</h1>
          <span className="badge badge-lg badge-neutral">{totalCount}</span>
          {isFetching && !isLoading && (
            <span className="loading loading-spinner loading-xs text-primary" />
          )}
        </div>
        {!isRoot && (
          <a href="/app/settings/agents" className="btn btn-primary btn-sm gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
            </svg>
            Invitar agente
          </a>
        )}
      </div>

      {/* Filters */}
      <div className="card bg-base-100 shadow-sm rounded-xl mb-6">
        <div className="card-body p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                  clipRule="evenodd"
                />
              </svg>
              <input
                type="text"
                className="input input-bordered w-full pl-10"
                placeholder="Buscar por nombre o email..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </div>

            {/* Role Filter */}
            <select
              className="select select-bordered w-full sm:w-56"
              value={roleFilter}
              onChange={(e) => handleRoleChange(e.target.value)}
            >
              {roleOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Mobile Cards (visible on small screens) */}
      <div className="flex flex-col gap-3 md:hidden">
        {isLoading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : users.length === 0 ? (
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body items-center text-center py-16">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-16 w-16 mb-4 opacity-30 text-base-content/50"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <p className="text-lg font-medium text-base-content/50">No se encontraron usuarios</p>
              <p className="text-sm text-base-content/40">
                {debouncedSearch || roleFilter
                  ? 'Intenta ajustar los filtros de busqueda'
                  : 'No hay usuarios registrados'}
              </p>
            </div>
          </div>
        ) : (
          users.map((u) => (
            <UserCard
              key={u.id}
              user={u}
              onEdit={() => setEditUser(u)}
              onChangePassword={() => setPasswordUser(u)}
              onToggleActive={() => toggleActiveMutation.mutate(u.id)}
              isToggling={toggleActiveMutation.isPending}
            />
          ))
        )}
      </div>

      {/* Desktop Table (hidden on small screens) */}
      <div className="card bg-base-100 shadow-sm rounded-xl overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="table table-zebra">
            <thead>
              <tr className="bg-base-200/50">
                <th className="font-semibold">Usuario</th>
                <th className="font-semibold">Email</th>
                <th className="font-semibold">Rol</th>
                <th className="font-semibold">Estado</th>
                <th className="font-semibold">Creado</th>
                <th className="font-semibold text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <>
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                </>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <div className="flex flex-col items-center justify-center py-16 text-base-content/50">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-16 w-16 mb-4 opacity-30"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                      <p className="text-lg font-medium">No se encontraron usuarios</p>
                      <p className="text-sm mt-1">
                        {debouncedSearch || roleFilter
                          ? 'Intenta ajustar los filtros de busqueda'
                          : 'No hay usuarios registrados en el sistema'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id} className="hover:bg-base-200/30 transition-colors">
                    <td>
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${getAvatarColor(
                            u.first_name + u.last_name
                          )}`}
                        >
                          {getInitials(u.first_name, u.last_name)}
                        </div>
                        <span className="font-medium">
                          {u.first_name} {u.last_name}
                        </span>
                      </div>
                    </td>
                    <td className="text-base-content/70">{u.email}</td>
                    <td>
                      <span className={`badge badge-sm ${ROLE_BADGE_CLASS[u.role] || 'badge-ghost'}`}>
                        {ROLE_LABELS[u.role] || u.role}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-block h-2.5 w-2.5 rounded-full ${
                            u.is_active ? 'bg-success' : 'bg-error'
                          }`}
                        />
                        <span className="text-sm">
                          {u.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                    </td>
                    <td className="text-base-content/70 text-sm">
                      {formatDate(u.created_at)}
                    </td>
                    <td className="text-right">
                      <ActionsDropdown
                        user={u}
                        onEdit={() => setEditUser(u)}
                        onChangePassword={() => setPasswordUser(u)}
                        onToggleActive={() => toggleActiveMutation.mutate(u.id)}
                        isToggling={toggleActiveMutation.isPending}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination (inside table card for desktop) */}
        {totalCount > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t border-base-200">
            <span className="text-sm text-base-content/60">
              Mostrando {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, totalCount)} de{' '}
              {totalCount} usuarios
            </span>
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </div>

      {/* Pagination (outside cards for mobile) */}
      {totalCount > 0 && (
        <div className="flex flex-col items-center gap-3 mt-4 md:hidden">
          <span className="text-sm text-base-content/60">
            Mostrando {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, totalCount)} de{' '}
            {totalCount} usuarios
          </span>
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}

      {/* Edit Modal */}
      <EditModal
        user={editUser}
        isRoot={isRoot}
        isOpen={!!editUser}
        onClose={() => setEditUser(null)}
        onSave={(data) => {
          if (editUser) {
            editMutation.mutate({ userId: editUser.id, data });
          }
        }}
        isSaving={editMutation.isPending}
      />

      {/* Password Modal */}
      <PasswordModal
        user={passwordUser}
        isOpen={!!passwordUser}
        onClose={() => setPasswordUser(null)}
        onSave={(newPassword) => {
          if (passwordUser) {
            passwordMutation.mutate({ userId: passwordUser.id, newPassword });
          }
        }}
        isSaving={passwordMutation.isPending}
      />
    </div>
  );
}

// --- Pagination Component ---

function Pagination({
  page,
  totalPages,
  onPageChange,
}: {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}): React.ReactElement {
  return (
    <div className="join">
      <button
        className="join-item btn btn-sm"
        disabled={page === 0}
        onClick={() => onPageChange(0)}
        title="Primera pagina"
      >
        &#171;
      </button>
      <button
        className="join-item btn btn-sm"
        disabled={page === 0}
        onClick={() => onPageChange(Math.max(0, page - 1))}
        title="Pagina anterior"
      >
        &#8249;
      </button>

      {Array.from({ length: totalPages }, (_, i) => i)
        .filter((i) => {
          if (i === 0 || i === totalPages - 1) return true;
          if (Math.abs(i - page) <= 1) return true;
          return false;
        })
        .reduce<(number | 'ellipsis')[]>((acc, curr, idx, arr) => {
          if (idx > 0) {
            const prev = arr[idx - 1];
            if (curr - prev > 1) {
              acc.push('ellipsis');
            }
          }
          acc.push(curr);
          return acc;
        }, [])
        .map((item, idx) =>
          item === 'ellipsis' ? (
            <button key={`ellipsis-${idx}`} className="join-item btn btn-sm btn-disabled">
              ...
            </button>
          ) : (
            <button
              key={item}
              className={`join-item btn btn-sm ${page === item ? 'btn-active' : ''}`}
              onClick={() => onPageChange(item)}
            >
              {item + 1}
            </button>
          )
        )}

      <button
        className="join-item btn btn-sm"
        disabled={page >= totalPages - 1}
        onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
        title="Pagina siguiente"
      >
        &#8250;
      </button>
      <button
        className="join-item btn btn-sm"
        disabled={page >= totalPages - 1}
        onClick={() => onPageChange(totalPages - 1)}
        title="Ultima pagina"
      >
        &#187;
      </button>
    </div>
  );
}
